(ns app.domain.planning-report.model
  (:require [app.domain.business.model :as business-model]
            [app.domain.form.model :as form-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.domain.system-of-units :as sou]
            [potpuri.core :as p]))


(def report?
  (eu/is-type-fn :entity.type/planning-draft-report
                 :entity.type/planning-final-report
                 :entity.type/planning-uploaded-report))


(def draft-report-editable-keys
  [:planning-report/draft-recommendation
   :planning-report/approval-comments
   :planning-report/rezoning-file
   :planning-report/previous-spc-file
   :planning-report/by-law
   :planning-report/revisions-and-clarifications
   :planning-report/template])


(def final-report-editable-keys
  [:planning-report/recommendation
   :planning-report/decision
   :planning-report/approval-comments
   :planning-report/rezoning-file
   :planning-report/previous-spc-file
   :planning-report/by-law
   :planning-report/template])


(def common-report-template-variables
  ["owner"
   "agent"
   "total-site-area"
   "zoning"
   "rezoning-file"
   "previous-spc-file"
   "by-law"
   "location"
   "conditions"
   "closed-conditions"
   "circulation-comments"
   "properties"
   "topics"])


(def draft-report-template-variables
  (concat common-report-template-variables
          ["draft-recommendation"
           "form-issues"
           "attachment-issues"
           "revisions-and-clarifications"]))


(def final-report-template-variables
  (concat common-report-template-variables
          ["recommendation"
           "decision"]))


(def properties-snippet-variables
  ["location"
   "roll-number"
   "legal-description"])


(def form-issue-snippet-variables
  ["form-name"
   "issue"])


(def attachment-issue-snippet-variables
  ["types"
   "issue"])


(def condition-snippet-variables
  ["title"
   "body"])


(def closed-condition-snippet-variables
  ["title"
   "body"
   "cleared"
   "cleared-by"])


(def topic-snippet-variables
  ["title"
   "comments"])


(def comment-snippet-variables
  ["title"
   "number"
   "body"])


(defn get-party-name [parties role-key]
  (if-some [party (->> parties
                       (filter #(party-model/has-role? % role-key))
                       first)]
    (if-some [business (:party/business party)]
      (business-model/business-name business i18n-model/default-lang) ; HACK: default-lang
      (let [name    (party-model/user-party-full-name party)
            company (party-model/get-user-party-user-key party :user/corporation-or-partnership)]
        (if-not (str/blank? company)
          (str name " (" company ")")
          name)))
    ""))


(defn get-total-site-area
  "Given the application, fetch the :site-area field and
  return it's value and unit in both metric and imperial systems as a string."
  [application]
  (p/if-all-let [[{:keys [unit]}
                  value] (form-model/get-field-attrs+field-val application :site-area)
                 _ (number? value)]
    (let [base          (sou/round sou/default-precision value)
          metric        (sou/convert-value unit :metric base)
          metric-unit   (-> (sou/unit-info unit :metric)
                            :label)
          imperial      (sou/convert-value unit :imperial base)
          imperial-unit (-> (sou/unit-info unit :imperial)
                            :label)]
      (str metric " " metric-unit " (" imperial " " imperial-unit ")"))
    "-"))


(defn get-completed-comments [application]
  (->> application
       (eu/descendants :entity.type/approval)
       (filter (du/in-state-fn :approval.state/complete))))


(defn get-new-comments [application already-synced-comment-ids]
  (->> application
       get-completed-comments
       (filter #(not (contains? already-synced-comment-ids (:db/id %))))
       set))


(defn completed-comments?
  "Returns truthy if there are completed approval comments for an application"
  [application]
  (->> application
       get-completed-comments
       seq
       boolean))


(defn new-comments?
  "Returns truthy if there are unsynced comments for a given draft report."
  [application report-id]
  (let [report                     (-> (concat (eu/descendants :entity.type/planning-draft-report application)
                                               (eu/descendants :entity.type/planning-final-report application))
                                       (p/find-first {:db/id report-id}))
        already-synced-comment-ids (->> report
                                        :planning-report/synced-approvals
                                        (map :db/id)
                                        set)]
    (-> (get-new-comments application already-synced-comment-ids)
        seq
        boolean)))

(defn concat-zoning [properties]
  (or (->> properties
           (map :property/zoning)
           distinct
           (str/join " / "))
      "-"))
